/**
 * Token store for the admin SPA.
 *
 * Storage: sessionStorage — survives page refresh, lost on tab close.
 * Safer than localStorage (no cross-tab leakage) and sufficient for admin
 * sessions (1h refresh-token TTL by design; users re-auth often anyway).
 *
 * Shape persisted:
 *   - access_token: JWT, 15 min TTL
 *   - refresh_token: opaque, 1 hour TTL (admin), rotating
 *   - id_token: JWT identity claims
 *   - expires_at: epoch ms when access_token expires
 *
 * On every read, the `expires_at` is checked; callers can use needsRefresh()
 * to decide whether to refresh proactively.
 */

const STORAGE_KEY = 'tacosai-admin-tokens';
const REFRESH_BUFFER_MS = 60_000; // refresh 60s before expiry

export interface StoredTokens {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  expires_at: number; // epoch ms
  scope?: string;
}

let cached: StoredTokens | null = null;

function readFromStorage(): StoredTokens | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (
      typeof parsed.access_token === 'string' &&
      typeof parsed.refresh_token === 'string' &&
      typeof parsed.expires_at === 'number'
    ) {
      return parsed as StoredTokens;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getTokens(): StoredTokens | null {
  if (cached) return cached;
  cached = readFromStorage();
  return cached;
}

export function setTokens(t: StoredTokens): void {
  cached = t;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch (e) {
    // sessionStorage can throw in private-mode Safari; degrade to in-memory
    console.warn('[token-store] sessionStorage write failed; in-memory only', e);
  }
}

export function clearTokens(): void {
  cached = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasValidAccessToken(): boolean {
  const t = getTokens();
  if (!t) return false;
  return Date.now() < t.expires_at - REFRESH_BUFFER_MS;
}

export function needsRefresh(): boolean {
  const t = getTokens();
  if (!t) return false;
  return Date.now() >= t.expires_at - REFRESH_BUFFER_MS;
}

/**
 * Build the StoredTokens shape from an /oidc/token response.
 *
 * The spec response shape:
 *   { access_token, refresh_token, id_token?, token_type, expires_in, scope? }
 * expires_in is seconds-until-expiry; we convert to absolute epoch ms.
 */
export function tokensFromResponse(resp: Record<string, unknown>): StoredTokens {
  const access_token = String(resp['access_token'] ?? '');
  const refresh_token = String(resp['refresh_token'] ?? '');
  const id_token = typeof resp['id_token'] === 'string' ? resp['id_token'] : undefined;
  const expires_in = typeof resp['expires_in'] === 'number' ? resp['expires_in'] : 900;
  const scope = typeof resp['scope'] === 'string' ? resp['scope'] : undefined;
  if (!access_token || !refresh_token) {
    throw new Error('Invalid token response: missing access_token or refresh_token');
  }
  return {
    access_token,
    refresh_token,
    id_token,
    expires_at: Date.now() + expires_in * 1000,
    scope,
  };
}

/** Decoded id_token claims of interest. Caller-side decode; no signature check (server already validates). */
export interface IdTokenClaims {
  sub: string;
  username?: string;
  email?: string;
  is_admin?: boolean;
  preferred_username?: string;
  exp?: number;
}

export function decodeIdToken(id_token: string | undefined): IdTokenClaims | null {
  if (!id_token) return null;
  try {
    const parts = id_token.split('.');
    if (parts.length !== 3) return null;
    const payloadB64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as IdTokenClaims;
  } catch {
    return null;
  }
}
